import { FunctionComponent, useMemo, useState } from "react";

import {
  GenericSelect,
  Option,
  OptionsSelectProps,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";

import { getPagesResult } from "@libs/paging/paging.utils";
import {
  ApplicationType,
  Tenant
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import { useTenantsPltQuery } from "@libs/api/gateways/plt/plt-gateway.hooks";
import { SITE_ID_PREFIX } from "@libs/api/types/common-dtos";

export interface TenantPickerProps
  extends Pick<
    OptionsSelectProps,
    | "selectedKeys"
    | "onChangeSelectedKeys"
    | "label"
    | "onBlur"
    | "onFocus"
    | "placeholder"
  > {
  excludeKeys?: string[];
  applications?: ApplicationType[];
  country?: string;
}

export const TenantPicker: FunctionComponent<TenantPickerProps> = ({
  excludeKeys,
  applications,
  country,
  placeholder,
  ...restOptionsSelectProps
}) => {
  const theme = useTheme();
  const [name, setName] = useState<string | undefined>();

  const query = useMemo(() => {
    const baseQuery = { applications, countries: country ? [country] : [] };
    if (name) {
      if (name.startsWith(SITE_ID_PREFIX)) {
        return {
          ...baseQuery,
          crmId: name
        };
      } else {
        return { ...baseQuery, name };
      }
    }

    return baseQuery;
  }, [applications, country, name]);

  const {
    isLoading,
    error,
    data,
    fetchNextPage,
    hasNextPage
  } = useTenantsPltQuery({
    isInactive: false,
    ...query
  });

  const options: Option<Tenant>[] = getPagesResult(data)
    .map(d => ({ key: d.id, text: d.name, data: d }))
    .filter(d => {
      if (!excludeKeys || excludeKeys.length === 0) {
        return true;
      } else {
        return !excludeKeys.includes(d.key);
      }
    });

  return (
    <GenericSelect
      searchBoxProps={{ onSearch: setName, removeSpecialCharacters: false }}
      options={options}
      loading={isLoading}
      errorMessage={error?.message}
      styles={{ root: { width: 250 } }}
      placeholder={placeholder ?? "Search by name or crmId"}
      onRenderOption={(option: Option<Tenant>) => (
        <Stack>
          <Text>{option.text}</Text>
          {option.data?.crmId && (
            <Text
              variant="small"
              styles={{ root: { color: theme.palette.themePrimary } }}
            >
              Crm ID: {option.data?.crmId}
            </Text>
          )}
        </Stack>
      )}
      showMoreOptionProps={{
        initialNumOfOptions: !hasNextPage ? options.length : options.length - 1,
        onClickShowMore: () => fetchNextPage()
      }}
      {...restOptionsSelectProps}
    />
  );
};
